import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import { useSelector } from "react-redux";
import { checkPermission, hasAdminAccess, permissionType } from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import {
  COMPANY_DETAILS_INITIAL_FORM,
  formatCompanyDetailsField,
  validateFinanceCompanyDetailsForm,
} from "../../../app/utils/company/companyDetailsFormUtils";
import { compressImageFile } from "../../../app/utils/common/compressImageFile";
import { createFinanceSalaryCompany } from "../../../app/utils/company/financeSalaryCompaniesStorage";
import "./CompanyDetailsFormModal.scss";

const CompanyDetailsFormModal = ({ open, onClose, onSuccess }) => {
  const { user } = useSelector((state) => state.auth.auth);
  const [form, setForm] = useState(COMPANY_DETAILS_INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const canCreateCompany =
    hasAdminAccess(user) ||
    checkPermission(permissionType.COMPANY_DETAILS, "create") ||
    checkPermission(permissionType.FINANCE_MANAGEMENT, "create");

  useEffect(() => {
    if (open) {
      setForm(COMPANY_DETAILS_INITIAL_FORM);
      setErrors({});
    }
  }, [open]);

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: formatCompanyDetailsField(key, value) }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const onSignatureChange = async (event) => {
    const file = event.target.files?.[0];
    setErrors((current) => ({ ...current, signatureFileName: "" }));

    if (!file) {
      updateField("signatureFileName", "");
      updateField("signatureDataUrl", "");
      return;
    }

    const allowedType = !file.type || ["image/jpeg", "image/jpg", "image/png"].includes(file.type);
    const allowedExtension = /\.(jpe?g|png)$/i.test(file.name);

    if (!allowedType || !allowedExtension) {
      setErrors((current) => ({
        ...current,
        signatureFileName: "Signature must be jpg, jpeg, or png.",
      }));
      event.target.value = "";
      return;
    }

    setSignatureLoading(true);
    try {
      const { dataUrl, fileName } = await compressImageFile(file);
      setForm((current) => ({
        ...current,
        signatureFileName: fileName,
        signatureDataUrl: dataUrl,
      }));
    } catch {
      showErrorToast("Could not process signature image. Try a smaller JPG or PNG.");
      event.target.value = "";
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canCreateCompany || saving) {
      if (!canCreateCompany) showErrorToast("You do not have permission to create a company.");
      return;
    }

    const validationErrors = validateFinanceCompanyDetailsForm(form);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      showErrorToast(Object.values(validationErrors)[0]);
      return;
    }

    setSaving(true);
    try {
      const saved = await createFinanceSalaryCompany(form);
      showSuccessToast("Company created successfully.");
      onSuccess?.(saved);
      onClose?.();
    } catch (error) {
      showErrorToast(
        error?.response?.data?.error?.message || error?.message || "Failed to save company."
      );
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (key, label, placeholder, options = {}) => (
    <div className={options.fullWidth ? "md:col-span-2" : ""}>
      <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
        {label}
        {options.required !== false ? <span className="text-red-500"> *</span> : null}
      </label>
      <input
        type={options.type || "text"}
        value={form[key]}
        onChange={(e) => updateField(key, e.target.value)}
        placeholder={placeholder}
        className={`search-input w-full !text-black ${errors[key] ? "!border-red-400" : ""}`}
      />
      {errors[key] ? <p className="mt-1 text-xs text-red-500">{errors[key]}</p> : null}
    </div>
  );

  return (
    <Modal
      className="company-details-form-modal"
      title="Add company"
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(560px, 96vw)"
      centered
      destroyOnClose
    >
      <form onSubmit={handleSubmit} className="company-details-form-modal__body space-y-5">
        <p className="text-sm text-[#6f7d97]">
          Used for salary sheets and payslips. Required fields are marked with *.
        </p>

        <div className="grid grid-cols-1 gap-4">
          {renderInput("companyName", "Name", "ABC Pvt Ltd")}
          {renderInput("ownerName", "Owner Name", "Himanshu Goswami")}
          {renderInput("contactNumber", "Contact Number", "9876543210")}
          {renderInput("emailId", "Email ID", "admin@abc.com, billing@abc.com")}

          <div>
            <label className="block text-sm font-medium text-[#5f6d8a] mb-1">
              Address <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.companyAddress}
              onChange={(e) => updateField("companyAddress", e.target.value)}
              placeholder="Mumbai, India"
              rows={3}
              className={`search-input w-full !h-auto !text-black !border !border-[#d7deed] !rounded-lg ${
                errors.companyAddress ? "!border-red-400" : ""
              }`}
            />
            {errors.companyAddress ? (
              <p className="mt-1 text-xs text-red-500">{errors.companyAddress}</p>
            ) : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-[#5f6d8a] mb-2">Signature</label>
            <div className="rounded-xl border border-[#e6ebf6] bg-[#f9fbff] p-4">
              <input
                type="file"
                accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                onChange={onSignatureChange}
                disabled={signatureLoading}
              />
              <p className="mt-2 text-xs text-[#6f7d97]">
                Optional. JPG or PNG for payslip footer{signatureLoading ? " (compressing…)" : ""}.
              </p>
              {errors.signatureFileName ? (
                <p className="mt-1 text-xs text-red-500">{errors.signatureFileName}</p>
              ) : null}
              {form.signatureDataUrl ? (
                <div className="mt-4 flex items-center gap-4">
                  <img
                    src={form.signatureDataUrl}
                    alt="Signature preview"
                    className="h-16 max-w-[220px] object-contain rounded border border-[#e6ebf6] bg-white p-2"
                  />
                  <span className="text-sm text-[#5f6d8a]">{form.signatureFileName}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2 border-t border-[#e6ebf6]">
          <button
            type="button"
            onClick={onClose}
            className="button border border-[#d7deed] bg-white text-[#27314a] px-5 py-2 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="button bg-btn-primary text-white px-5 py-2 rounded-lg"
            disabled={!canCreateCompany || saving}
          >
            {saving ? "Saving…" : "Save company"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CompanyDetailsFormModal;
